<template>
  <div class="preika-auth-page preika-login-page">
    <main class="preika-auth-shell">
      <section class="preika-login-showcase" aria-label="PREIKAのご案内">
        <div class="preika-login-showcase__grid" aria-hidden="true"></div>

        <header class="preika-login-showcase__header">
          <div class="preika-login-wordmark" aria-label="PREIKA AKI-CORP">
            <span class="preika-login-wordmark__name">PREIKA</span>
            <span class="preika-login-wordmark__line"></span>
            <span class="preika-login-wordmark__company">AKI-CORP</span>
          </div>

          <span class="preika-login-showcase__badge">
            AKI INTERNAL WORKSPACE
          </span>
        </header>

        <div class="preika-login-showcase__visual" aria-hidden="true">
          <img
            v-if="imageAvailable"
            src="/img/auth/preika_login_workspace.png"
            alt=""
            class="preika-login-showcase__image"
            @error="imageAvailable = false"
          >
        </div>

        <div class="preika-login-showcase__copy">
          <p class="preika-login-showcase__eyebrow">WORKPLACE PLATFORM</p>
          <h1>
            日々の業務を、<br>
            <span>ひとつの場所へ。</span>
          </h1>
          <p class="preika-login-showcase__description">
            勤怠、月次報告、各種申請と承認をスムーズにつなぐ、
            AKIの社内業務プラットフォームです。
          </p>
        </div>

        <footer class="preika-login-showcase__footer">
          <span>PREIKA Workplace Platform</span>
          <span>© {{ currentYear }} AKI Inc.</span>
        </footer>
      </section>

      <section class="preika-login-form-panel">
        <div class="preika-login-form-wrap">
          <div class="preika-login-mobile-wordmark preika-login-wordmark" aria-label="PREIKA AKI-CORP">
            <span class="preika-login-wordmark__name">PREIKA</span>
            <span class="preika-login-wordmark__line"></span>
            <span class="preika-login-wordmark__company">AKI-CORP</span>
          </div>

          <div class="preika-login-form__meta">
            <span class="preika-login-form__secure">
              <CIcon name="cil-shield-alt"/>
              SECURE SIGN IN
            </span>
            <span class="preika-login-form__internal">社内専用</span>
          </div>

          <p class="preika-login-form__eyebrow">WELCOME BACK</p>
          <h2>PREIKAにログイン</h2>
          <p class="preika-login-form__description">
            登録されているメールアドレスとパスワードを入力してください。
          </p>

          <ValidationObserver ref="obs" v-slot="observerProps">
            <CForm
              class="preika-login-form"
              novalidate
              @submit.prevent="loginData"
            >
              <div
                v-if="errors.length"
                class="alert alert-danger"
                role="alert"
                aria-live="assertive"
              >
                <strong>ログインできませんでした</strong>
                <div
                  v-for="error in errors"
                  :key="error.error_id || error.error_name || error"
                >
                  {{ error.error_name || error }}
                </div>
              </div>

              <ValidationProvider
                name="メールアドレス"
                rules="required|email|max:254"
                v-slot="providerProps"
                mode="aggressive"
              >
                <div class="preika-login-field">
                  <label for="preika-login-email">メールアドレス</label>
                  <CInput
                    id="preika-login-email"
                    placeholder="name@example.com"
                    autocomplete="username email"
                    autofocus
                    v-model="email"
                    :is-valid="providerProps.errors.length ? false : null"
                    :aria-invalid="providerProps.errors.length ? 'true' : 'false'"
                  >
                    <template #prepend-content>
                      <CIcon name="cil-user"/>
                    </template>
                  </CInput>
                  <p v-if="providerProps.errors[0]" class="error">
                    {{ providerProps.errors[0] }}
                  </p>
                </div>
              </ValidationProvider>

              <ValidationProvider
                name="パスワード"
                rules="required|password"
                v-slot="providerProps"
                mode="aggressive"
              >
                <div class="preika-login-field">
                  <div class="preika-login-field__label-row">
                    <label for="preika-login-password">パスワード</label>
                    <router-link
                      :to="{ name: 'SendEmail' }"
                      class="preika-login-form__forgot"
                    >
                      パスワードを忘れた方
                    </router-link>
                  </div>

                  <CInput
                    id="preika-login-password"
                    placeholder="パスワードを入力"
                    :type="passwordInputType"
                    autocomplete="current-password"
                    v-model="password"
                    :is-valid="providerProps.errors.length ? false : null"
                    :aria-invalid="providerProps.errors.length ? 'true' : 'false'"
                  >
                    <template #prepend-content>
                      <CIcon name="cil-lock-locked"/>
                    </template>
                    <template #append-content>
                      <button
                        type="button"
                        class="preika-login-password-toggle"
                        :aria-pressed="showPassword ? 'true' : 'false'"
                        :aria-label="showPassword ? 'パスワードを非表示にする' : 'パスワードを表示する'"
                        @click="showPassword = !showPassword"
                      >
                        {{ showPassword ? '非表示' : '表示' }}
                      </button>
                    </template>
                  </CInput>

                  <p v-if="providerProps.errors[0]" class="error">
                    {{ providerProps.errors[0] }}
                  </p>
                </div>
              </ValidationProvider>

              <button
                type="submit"
                class="btn btn-primary preika-login-submit"
                :disabled="loading || observerProps.invalid || !observerProps.validated"
                :aria-busy="loading ? 'true' : 'false'"
              >
                <span v-if="loading" class="my-spinner" aria-hidden="true"></span>
                <span>{{ loading ? 'ログインしています' : 'PREIKAにログイン' }}</span>
              </button>
            </CForm>
          </ValidationObserver>

          <div class="preika-login-form__note">
            <span class="preika-login-form__note-icon">
              <CIcon name="cil-shield-alt"/>
            </span>
            <div>
              <strong>安全な接続で保護されています</strong>
              <p>共有端末では、利用後に必ずログアウトしてください。</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import ConstList from './../../ConstList'

export default {
  name: 'Login',

  data () {
    return {
      email: '',
      password: '',
      errors: [],
      loading: false,
      showPassword: false,
      imageAvailable: true
    }
  },

  computed: {
    currentYear () {
      return new Date().getFullYear()
    },

    passwordInputType () {
      return this.showPassword ? 'text' : 'password'
    }
  },

  methods: {
    loginData () {
      if (this.loading) return

      this.errors = []
      this.loading = true

      const params = new URLSearchParams()
      params.append(ConstList.EMAIL, this.email)
      params.append(ConstList.PASSWORD, this.password)

      axios
        .post(process.env.VUE_APP_API_ORIGIN + '/check', params)
        .then(response => {
          if (response.data.result === ConstList.SUCCESS) {
            sessionStorage.setItem('level', response.data.level)
            window.location.href = './#/dashboard'
            return
          }

          this.errors.push(ConstList.NORMAL_ERROR)
          this.$router.push({
            name: ConstList.ROUTE_ERROR
          })
        })
        .catch(error => {
          axios
            .get(`${process.env.VUE_APP_API_ORIGIN}/error/frontError/${error}`)
            .then(() => {
              alert(ConstList.SYSTEM_ERROR)
            })
            .catch(() => {
              alert(ConstList.SYSTEM_COMMUNICATION_ERROR)
            })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
